import { COUNTRIES } from '@/const/countries.const';

/** The countries a store can be created in (the 16 of `COUNTRIES`). */
export const SUPPORTED_COUNTRIES: readonly string[] = COUNTRIES.map(
  (country) => country.value
);

/** Venezuela is absent on purpose: it charges in Bs. or US$ and the owner must pick one. */
const COUNTRY_CURRENCY: ReadonlyMap<string, string> = new Map([
  ['AR', 'ARS'],
  ['BO', 'BOB'],
  ['BR', 'BRL'],
  ['CL', 'CLP'],
  ['CO', 'COP'],
  ['CR', 'CRC'],
  ['DO', 'DOP'],
  ['EC', 'USD'],
  ['ES', 'EUR'],
  ['MX', 'MXN'],
  ['PA', 'USD'],
  ['PE', 'PEN'],
  ['PY', 'PYG'],
  ['US', 'USD'],
  ['UY', 'UYU'],
]);

export function isSupportedCountry(code: string | null | undefined): boolean {
  return SUPPORTED_COUNTRIES.includes(code ?? '');
}

/** The store currency a country implies; null for VE (the owner chooses) and unknown codes. */
export function currencyForCountry(
  code: string | null | undefined
): string | null {
  return COUNTRY_CURRENCY.get(code ?? '') ?? null;
}

/** Number/date locale of a country: `pt-BR`, `en-US`, else `es-{code}`. */
export function countryLocale(code: string): string {
  if (code === 'BR') return 'pt-BR';
  if (code === 'US') return 'en-US';
  return `es-${code}`;
}

/**
 * Countries whose Mercado Pago link chain is complete end to end. Mirrors the
 * API gate `src/payments/mercadopago-countries.ts` exactly.
 */
export const MERCADOPAGO_COUNTRIES: ReadonlySet<string> = new Set(['AR']);

export function mercadoPagoAvailable(
  country: string | null | undefined
): boolean {
  return MERCADOPAGO_COUNTRIES.has((country ?? '').trim().toUpperCase());
}

const EXACT_TIME_ZONES: ReadonlyMap<string, string> = new Map(
  COUNTRIES.map((country) => [country.tz, country.value])
);

const TIME_ZONE_PATTERNS: ReadonlyArray<readonly [RegExp, string]> = [
  [/^America\/Argentina\//, 'AR'],
  [/^America\/Buenos_Aires$/, 'AR'],
  [
    /^America\/(Sao_Paulo|Recife|Fortaleza|Manaus|Belem|Bahia|Cuiaba|Campo_Grande|Porto_Velho|Boa_Vista|Rio_Branco|Araguaina|Maceio|Santarem|Noronha|Eirunepe)$/,
    'BR',
  ],
  [/^America\/(Santiago|Punta_Arenas)$/, 'CL'],
  [/^Pacific\/Easter$/, 'CL'],
  [/^(Europe\/Madrid|Africa\/Ceuta|Atlantic\/Canary)$/, 'ES'],
  [
    /^America\/(Mexico_City|Cancun|Merida|Monterrey|Matamoros|Chihuahua|Ciudad_Juarez|Ojinaga|Mazatlan|Bahia_Banderas|Hermosillo|Tijuana)$/,
    'MX',
  ],
  [/^Pacific\/Galapagos$/, 'EC'],
  [
    /^America\/(New_York|Chicago|Denver|Los_Angeles|Phoenix|Anchorage|Detroit|Boise)$/,
    'US',
  ],
  [/^America\/(Indiana|Kentucky)\//, 'US'],
  [/^Pacific\/Honolulu$/, 'US'],
];

/** The supported country of a device IANA time zone, or null when it maps to none. */
export function countryFromTimeZone(
  tz: string | null | undefined
): string | null {
  if (!tz) return null;
  const exact = EXACT_TIME_ZONES.get(tz);
  if (exact) return exact;
  const match = TIME_ZONE_PATTERNS.find(([pattern]) => pattern.test(tz));
  return match ? match[1] : null;
}
